import * as echarts from 'echarts/core';
import { TitleComponent, ToolboxComponent, TooltipComponent, GridComponent, LegendComponent } from 'echarts/components';
import { LineChart } from 'echarts/charts';
import { CanvasRenderer } from 'echarts/renderers';
import { xTickStore } from '../../ng-services/xtick.store';
import { EChartsType } from 'echarts/core';
import { BaseChartComponent } from './base-chart.component';
import { when } from 'mobx';

echarts.use([
    TitleComponent,
    ToolboxComponent,
    TooltipComponent,
    GridComponent,
    LegendComponent,
    LineChart,
    CanvasRenderer,
]);

class MiniDayChartComponent extends BaseChartComponent {
    values!: number[];
    dates!: Date[];

    symbol!: string;

    async whenDataReady(): Promise<boolean> {
        await when(() => Boolean(this.history && this.history.length > 0));
        return true;
    }

    async initializeChart(chart: EChartsType) {
        await when(() => Boolean(this.history));

        if (!this.history) {
            return;
        }

        // split data to separate value/date arrays
        this.values = this.history.map((h) => h.relative);
        this.dates = this.history.map((h) => new Date(h.timestamp));

        /*
            This is a solution to a bug (?) in Echarts
            The visualMap was crashing if the data didn't match pieces, so eg. if all data was > or < 0 - it would crash
            Solution:
         */

        // get min/max values
        const minValue = Math.min(...this.values);
        const maxValue = Math.max(...this.values);
        // check if the values are crossing 0 point
        const goesThroughZero = minValue < 0 && 0 < maxValue;
        // check if the whole chart is lt 0
        const lteZero = minValue <= 0 && maxValue <= 0;

        // if lt 0 - chart should be red; green otherwise (will get overriden by visualMap if chart crosses 0)
        const color = lteZero ? 'red' : 'green';

        // visual map - will only be used if chart passes through the 0 point
        const visualMap = {
            pieces: [
                {
                    gte: minValue - 1,
                    lt: 0,
                    color: 'red',
                },
                {
                    gt: 0,
                    lte: maxValue + 1,
                    color: 'green',
                },
            ],
            outOfRange: {
                color: 'transparent',
            },
            show: false,
        };

        chart.setOption({
            // enable visualMap only if chart passes through 0 point
            ...(goesThroughZero && { visualMap }),
            tooltip: {
                show: false,
                trigger: 'axis',
                position: function (pt: any) {
                    return [pt[0], '10%'];
                },
                formatter: function (params: { axisValue: string; value: number }[]) {
                    const { axisValue, value } = params[0];
                    const date = new Date(axisValue);
                    return `
                        <b>${date.toLocaleDateString()}</b><br/> ${value.toFixed(2)}
                    `;
                },
            },

            legend: {
                show: false,
            },
            grid: {
                show: false,
                top: 0,
                left: 0,
                bottom: 0,
                right: 0,
            },
            xAxis: {
                show: false,
                type: 'category',
                boundaryGap: false,
                data: this.dates,
                axisLabel: {
                    formatter: (value: string) => new Date(value).toLocaleDateString(),
                },
            },
            yAxis: {
                show: false,
                type: 'value',
                boundaryGap: false,
            },
            series: [
                {
                    showSymbol: false,
                    data: this.values,
                    type: 'line',
                    areaStyle: {},
                    itemStyle: {
                        color,
                    },
                    lineStyle: {
                        width: 0,
                    },
                },
            ],
        });
    }

    get isLoading() {
        return this.history === undefined;
    }

    get isHistoryEmpty() {
        return !this.isLoading && this.history && this.history?.length === 0;
    }

    get history() {
        return xTickStore.getHistory(this.symbol);
    }
}

angular.module('tradersyard').component('tyMiniDayChart', {
    // language=HTML
    template: `
        <div class="chart">
            <div class="chart__target target_chart_element" ng-if="!$ctrl.isHistoryEmpty"></div>
            <div class="chart_no_data" ng-if="$ctrl.isHistoryEmpty">No data available.</div>
        </div>
    `,
    controller: MiniDayChartComponent,
    bindings: {
        symbol: '<',
    },
});
